import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { Shell } from './components/Shell';
import { ArticleEditPage, ArticlesPage } from './pages/Articles';
import { CompetitionsPage, PlayersPage, TeamDetailPage, TeamsPage } from './pages/Catalog';
import { DashboardPage } from './pages/Dashboard';
import { AuditPage, BoardsPage, IssuesPage, SettingsPage } from './pages/Game';
import { FeedImportPage } from './pages/Feed';
import { MatchDataPage } from './pages/MatchData';
import { MatchDetailPage } from './pages/MatchDetail';
import { MatchesPage, MatchNewPage } from './pages/Matches';
import { LeagueDetailPage, LeaguesPage, UserDetailPage, UsersPage } from './pages/People';
import { SlatePage } from './pages/Slate';

const num = (v: unknown): number | undefined =>
  typeof v === 'number' ? v : typeof v === 'string' && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : undefined;
const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v !== '' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : undefined;

const root = createRootRoute({ component: Shell, notFoundComponent: () => <div className="page">Page not found.</div> });

const dashboard = createRoute({ getParentRoute: () => root, path: '/', component: DashboardPage });
const slate = createRoute({
  getParentRoute: () => root,
  path: '/slate',
  validateSearch: (s: Record<string, unknown>): { round?: number } => ({ round: num(s.round) }),
  component: SlatePage,
});
const matches = createRoute({
  getParentRoute: () => root,
  path: '/matches',
  validateSearch: (s: Record<string, unknown>): { q?: string; status?: string; competitionId?: number; roundId?: number; published?: string } => ({
    q: str(s.q),
    status: str(s.status),
    competitionId: num(s.competitionId),
    roundId: num(s.roundId),
    published: str(s.published),
  }),
  component: MatchesPage,
});
const matchNew = createRoute({
  getParentRoute: () => root,
  path: '/matches/new',
  validateSearch: (s: Record<string, unknown>): { roundId?: number } => ({ roundId: num(s.roundId) }),
  component: MatchNewPage,
});
const matchImport = createRoute({
  getParentRoute: () => root,
  path: '/matches/import',
  validateSearch: (s: Record<string, unknown>): { date?: string } => ({ date: str(s.date) }),
  component: FeedImportPage,
});
const matchDetail = createRoute({ getParentRoute: () => root, path: '/matches/$matchId', component: MatchDetailPage });
const matchData = createRoute({ getParentRoute: () => root, path: '/matches/$matchId/data', component: MatchDataPage });
const articlesList = createRoute({
  getParentRoute: () => root,
  path: '/articles',
  validateSearch: (s: Record<string, unknown>): { q?: string; kind?: string; status?: string } => ({ q: str(s.q), kind: str(s.kind), status: str(s.status) }),
  component: ArticlesPage,
});
const articleNew = createRoute({
  getParentRoute: () => root,
  path: '/articles/new',
  validateSearch: (s: Record<string, unknown>): { matchId?: number; kind?: string } => ({ matchId: num(s.matchId), kind: str(s.kind) }),
  component: ArticleEditPage,
});
const articleEdit = createRoute({ getParentRoute: () => root, path: '/articles/$articleId', component: ArticleEditPage });
const competitions = createRoute({ getParentRoute: () => root, path: '/competitions', component: CompetitionsPage });
const teams = createRoute({ getParentRoute: () => root, path: '/teams', component: TeamsPage });
const teamDetail = createRoute({ getParentRoute: () => root, path: '/teams/$teamId', component: TeamDetailPage });
const players = createRoute({ getParentRoute: () => root, path: '/players', component: PlayersPage });
const users = createRoute({
  getParentRoute: () => root,
  path: '/users',
  validateSearch: (s: Record<string, unknown>): { q?: string; role?: string; banned?: string; page?: number } => ({
    q: str(s.q),
    role: str(s.role),
    banned: str(s.banned),
    page: num(s.page),
  }),
  component: UsersPage,
});
const userDetail = createRoute({ getParentRoute: () => root, path: '/users/$userId', component: UserDetailPage });
const leagues = createRoute({ getParentRoute: () => root, path: '/leagues', component: LeaguesPage });
const leagueDetail = createRoute({ getParentRoute: () => root, path: '/leagues/$leagueId', component: LeagueDetailPage });
const boards = createRoute({ getParentRoute: () => root, path: '/boards', component: BoardsPage });
const issues = createRoute({ getParentRoute: () => root, path: '/issues', component: IssuesPage });
const settings = createRoute({ getParentRoute: () => root, path: '/settings', component: SettingsPage });
const audit = createRoute({ getParentRoute: () => root, path: '/audit', component: AuditPage });

const routeTree = root.addChildren([
  dashboard,
  slate,
  matches,
  matchNew,
  matchImport,
  matchDetail,
  matchData,
  articlesList,
  articleNew,
  articleEdit,
  competitions,
  teams,
  teamDetail,
  players,
  users,
  userDetail,
  leagues,
  leagueDetail,
  boards,
  issues,
  settings,
  audit,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
